import {
  createFileRoute,
  Link,
  Navigate,
  Outlet,
  useRouterState,
} from "@tanstack/react-router";
import {
  BookOpenIcon,
  FlagIcon,
  FolderTreeIcon,
  GaugeIcon,
  LogsIcon,
  ShieldIcon,
  UsersIcon,
} from "lucide-react";
import { AppShell } from "@/components/layout/AppShell";
import { useAuth } from "@/lib/auth-context";
import { useMe } from "@/lib/query-hooks";

export const Route = createFileRoute("/admin")({
  head: () => ({
    meta: [
      { title: "Admin · PN-CivilServices" },
      { name: "description", content: "Manage exams, subjects, files, and reports." },
      { property: "og:title", content: "Admin · PN-CivilServices" },
      { property: "og:description", content: "Content management for the ledger." },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: AdminLayout,
});

const NAV = [
  { to: "/admin", label: "Dashboard", icon: GaugeIcon, exact: true },
  { to: "/admin/exams", label: "Exams", icon: BookOpenIcon },
  { to: "/admin/reports", label: "Reports", icon: FlagIcon },
  { to: "/admin/users", label: "Admin users", icon: UsersIcon, super: true },
  { to: "/admin/audit-log", label: "Audit log", icon: LogsIcon, super: true },
];

function AdminLayout() {
  const { user, loading } = useAuth();
  const { data: me, isLoading: meLoading } = useMe();
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  if (!loading && !user) return <Navigate to="/login" />;
  if (meLoading) return <AppShell><div className="p-10 text-ink-muted">Checking access…</div></AppShell>;
  const isAdmin = me?.role === "admin" || me?.role === "super_admin";
  if (!isAdmin) {
    return (
      <AppShell>
        <div className="max-w-md mx-auto mt-24 text-center card-ledger p-8">
          <ShieldIcon className="mx-auto h-8 w-8 text-paprika" />
          <h1 className="mt-4 font-display text-2xl">Admin only</h1>
          <p className="mt-2 text-ink-soft text-sm">
            Your account doesn't have admin privileges.
          </p>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <div className="mx-auto grid max-w-7xl grid-cols-12 gap-6 px-4 pt-8 pb-16 sm:px-6 lg:px-8">
        <aside className="col-span-12 md:col-span-3 lg:col-span-2">
          <div className="label-card mb-3 flex items-center gap-1.5">
            <ShieldIcon className="h-3 w-3" /> Admin
          </div>
          <nav className="card-ledger overflow-hidden">
            {NAV.filter((n) => !n.super || me?.role === "super_admin").map(
              (n) => {
                const active = n.exact
                  ? pathname === n.to
                  : pathname.startsWith(n.to);
                return (
                  <Link
                    key={n.to}
                    to={n.to}
                    className={`flex items-center gap-2 px-4 py-2.5 text-sm hairline-b last:border-b-0 ${
                      active
                        ? "bg-surface-alt text-paprika font-semibold"
                        : "hover:bg-surface-alt/60"
                    }`}
                  >
                    <n.icon className="h-4 w-4" />
                    {n.label}
                  </Link>
                );
              },
            )}
          </nav>
        </aside>
        <section className="col-span-12 md:col-span-9 lg:col-span-10 min-w-0">
          <Outlet />
        </section>
      </div>
    </AppShell>
  );
}

export { FolderTreeIcon }; // silence unused import
